import { Intent } from '@blueprintjs/core'
import Countries from '../../../../../constants/Countries'
import USAStates from '../../../../../constants/USAStates'
import { CurrentState, OrganizationShippingSettings } from '../../../../../current/types'
import { Address } from '../../../../../shipping'
import { OrderItem, OrderWithShipments } from '../../../../../types/api'
import { DEFAULT_EEL_PFC, FREE_INSURANCE_AMOUNT } from './constants'
import { DeliveryConfirmation } from './PackageConstants'
import {
  CountryOption,
  CustomsInfo,
  CustomsInfoContentsType,
  CustomsInfoNonDeliveryOption,
  CustomsInfoRestrictionType,
  UsaStateInfo
} from './types'

export const getAddressErrors = (address: Address) => {
  const errors: any = {}

  if (!address.name && !address.company) errors.name = 'is required'
  if (!address.street1) errors.street1 = 'is required'
  if (!address.city) errors.city = 'is required'
  if (!address.state) errors.state = 'is required'
  if (!address.zip) errors.zip = 'is required'
  if (!address.phone) errors.phone = 'is required'

  return Object.keys(errors).length > 0 ? errors : null
}

export const hasPoBox = (street?: string) => {
  if (!street) return false

  const matches = street.match(/P[.]?O[.]?\s*BOX/i)
  return matches ? matches.length > 0 : false
}

const UsaStateMap = USAStates.reduce(
  (acc, curr: UsaStateInfo) => ({
    ...acc,
    [curr.name.toUpperCase()]: curr.abbreviation.toUpperCase(),
    [curr.abbreviation.toUpperCase()]: curr.abbreviation.toUpperCase()
  }),
  {}
)

const CountryMap = Countries.reduce(
  (acc, curr: CountryOption) => ({
    ...acc,
    [curr.name.toUpperCase()]: curr.abbreviation.toUpperCase(),
    [curr.abbreviation.toUpperCase()]: curr.abbreviation.toUpperCase()
  }),
  {}
)
// adding aliases here
CountryMap['UNITED STATES'] = 'US'
CountryMap['USA'] = 'US' // eslint-disable-line dot-notation

export const shouldAddCustomsInfo = (countryA?: string, countryB?: string): boolean => {
  if (!countryA || !countryB) return false

  const entryA = CountryMap[countryA.toUpperCase()]
  const entryB = CountryMap[countryB.toUpperCase()]
  return entryA && entryB && CountryMap[entryA] !== CountryMap[entryB]
}

export const resolveStateValue = (value: string) => (value && UsaStateMap[value.toUpperCase()]) || 'Choose a state...'
export const resolveCountryValue = (value: string) => {
  return value && CountryMap[value.toUpperCase()]
}

export function isValidPostalCode(postalCode: string, countryCode = 'US'): boolean {
  let postalCodeRegex = /^(?:[A-Z0-9]+([- ]?[A-Z0-9]+)*)?$/

  switch (countryCode) {
    case 'CA':
      postalCodeRegex = /^([A-Za-z][0-9][A-Za-z])\s*([0-9][A-Za-z][0-9])$/
      break
    case 'US':
      postalCodeRegex = /^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/
      break
    default:
      break
  }
  return postalCodeRegex.test(postalCode)
}

export const removeAllNonDigits = (number: string) => {
  if (!number) return ''
  return number.replace(/\D/g, '')
}

export function requireSignatureFromSettings(
  settings?: OrganizationShippingSettings,
  orderSubtotal?: string | number
): DeliveryConfirmation {
  if (!settings) return DeliveryConfirmation.Off
  if (settings.signatureRequiredAmount === null || settings.signatureRequiredAmount === undefined)
    return DeliveryConfirmation.Off

  if (+orderSubtotal >= +settings.signatureRequiredAmount) return DeliveryConfirmation.Signature

  return DeliveryConfirmation.Off
}

export function getDeclaredValue(
  settings: OrganizationShippingSettings,
  subtotal: number,
  currencyCode: string
): number {
  const insuranceThreshold = settings.insuranceThreshold ? +settings.insuranceThreshold : FREE_INSURANCE_AMOUNT

  // Insurance only works with USD
  if (currencyCode !== 'USD') return null

  // allow admins to control insurance
  if (+subtotal < insuranceThreshold) return null

  return +subtotal
}

export function getRoundedDeclaredValueString(
  settings: OrganizationShippingSettings,
  subtotal: number,
  currencyCode: string
) {
  const value = getDeclaredValue(settings, subtotal, currencyCode)
  if (value == null) return null
  return value.toFixed(2)
}

export const validationIntent = (
  canValidate: boolean,
  validation: boolean,
  invalidIntent: Intent,
  validIntent: Intent = Intent.NONE
): Intent => {
  if (!canValidate) return validIntent
  return validation ? validIntent : invalidIntent
}

export const validationColor = (
  canValidate: boolean,
  validation: boolean,
  invalidColor: string,
  validColor?: string
): string => {
  if (!canValidate) return validColor
  return validation ? validColor : invalidColor
}

/**
 * Builds the customs info for a shipment
 * Follows this guide https://www.easypost.com/customs-guide
 * Can mostly leave alone except for orderless shipments
 */
export function buildDefaultCustomsInfo(
  current: CurrentState,
  isReturn: boolean,
  order?: OrderWithShipments
): CustomsInfo {
  const info: CustomsInfo = {
    contentsType: isReturn ? CustomsInfoContentsType['Returned Goods'] : CustomsInfoContentsType.Merchandise,
    restrictionType: CustomsInfoRestrictionType.None,
    customsCertify: true,
    customsSigner: current.user.name,
    nonDeliveryOption: CustomsInfoNonDeliveryOption.Return,
    eelPfc: DEFAULT_EEL_PFC,
    customsItems: []
  }

  // Set parcelNumber according to which shipment the orderItem belongs to
  if (order) {
    const orderItemIdsToParcelNumbers = {}
    order.shipments.forEach((shipment, i) => {
      shipment.orderItems.forEach(orderItem => {
        orderItemIdsToParcelNumbers[orderItem.id] = i + 1 // parcelNumber is 1-indexed here
      })
    })

    info.eelPfc = +order.subtotal < 2500 ? DEFAULT_EEL_PFC : undefined
    info.customsItems = order.orderItems
      .map((oi: OrderItem) => {
        if (oi.pickedStatus === 'EXCLUDED_FROM_PACKING') {
          return null
        }

        return {
          hsTariffNumber: '',
          description: oi.name,
          quantity: oi.quantity,
          weight: oi.productWeight,
          value: oi.total,
          originCountry: resolveCountryValue(current.organization.address.country),
          parcelNumber: orderItemIdsToParcelNumbers[oi.id] || 1 // parcelNumber is 1-indexed here
          // default to parcelNumber 1, because this is the current behavior.
          // orderItemIdsToParcelNumbers[oi.id] will be null if they didn't assign the item to any shipment on the packing screen
        }
      })
      .filter(Boolean) // remove null (EXCLUDED_FROM_PACKING) items
  }

  return info
}

/**
 *
 * @param sm an order's shipping method
 * @returns human readable shipping method
 * @example USPSPriorityMailInternationalSmallFlatRateBox -> USPS Priority Mail International Small Flat Rate Box
 */
export function formatShippingMethod(sm?: string): string {
  if (!sm) return 'Other'

  return sm
    .replace('CA_', '')
    .replace('US_', '')
    .replace(/([A-Z0-9])/g, ' $1')
    .trim()
    .replace('Fed Ex', 'FedEx')
    .replace('U S P S', 'USPS')
    .replace('U P S', 'UPS')
    .replace('U S A', 'USA')
    .replace('e Bay', 'eBay')
    .replace('D H L', 'DHL')
}

export const validCustomsItemWeight = weight => {
  return parseFloat(weight?.toString()) > 0
}

/**
 * Checks if all order items are media items
 * @param orderItems Array of order items to check
 * @returns true if all items have mediaItem flag set to true
 */
export function isAllMediaItems(orderItems: OrderItem[]): boolean {
  if (!orderItems || orderItems.length === 0) return false
  return orderItems.every(item => item.mediaItem === true)
}

export const CARRIER_ALIASES: Record<string, string> = {
  FedExDefault: 'FedEx',
  UPSDAP: 'UPS'
}

export const normalizeCarrierName = (carrier: string): string => CARRIER_ALIASES[carrier] || carrier
